import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@ui/lib/utils";

// YANGO DELI button primitive (BF-578, re-skinned BF-733). Rounded corners
// (--radius, rounded-lg), tokened fills, `.type-button-md` label, no shadow tier.
// `default` is the design's `button-primary` (brand-yellow fill / black label via
// --primary-foreground); `secondary` is `button-secondary` (surface fill / ink
// text / 1px form-control (--input) border). The extra variants
// (destructive/outline/ghost/link) are retained so existing consumers keep their
// public API — restyled to the Yango Deli language (colour resolves through the
// retuned semantic tokens; no API/variant change).
//
// Touch hit area (BF-604, R4 §4.1): on the touch tiers (below `desktop:`) every button
// keeps a >=44px (--spacing * 11) hit area WITHOUT changing its rendered size — an
// invisible absolutely-positioned ::before that grows only the axis that is short of
// 44px (`min(0px, (100% - 44px) / 2)` per edge; a wide button gains no inline overlap
// with its neighbours). `relative` anchors it; a consumer's own `absolute`/`fixed`
// still wins through cn(), and anchors the overlay just the same.
const buttonVariants = cva(
  "relative max-desktop:before:absolute max-desktop:before:inset-[min(0px,calc((100%-var(--spacing)*11)/2))] type-button-md inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg transition-[color,background-color,border-color] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-ring/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
  {
    variants: {
      variant: {
        // button-primary — brand-yellow fill, black label (--primary-foreground), no border.
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        // functional status colour (semantic hue, not brand yellow).
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // button-secondary — surface canvas, ink text, 1px form-control (--input) border.
        secondary: "border border-input bg-background text-foreground hover:bg-secondary",
        outline:
          "border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // link — ink text (brand yellow at 1.32:1 on white fails WCAG); brand colour stays on filled CTAs.
        link: "text-foreground underline-offset-4 hover:underline",
      },
      size: {
        // 36px height per the reference (button-primary height: 36px). Sizes are
        // unchanged from the original primitive (restyle, not a resize).
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
